import { useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2, Clock3, Info, MessageCircle } from "lucide-react";
import { Link, Navigate, useLocation } from "react-router-dom";
import StitchFlowLayout from "@/components/stitch/StitchFlowLayout";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getProgramFromSearch } from "@/content/consultingPrograms";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const requestStatusLabels = {
  submitted: "Davet kodunuz hazırlanıyor",
  reviewed: "Davet kodunuz hazırlanıyor",
  code_redeemed: "Davet kodunuz kullanıldı",
  approved: "Programınız açıldı",
  declined: "Talebiniz sonuçlandırıldı",
};

const SUPPORT_WHATSAPP_URL = `https://wa.me/905327991130?text=${encodeURIComponent(
  "Merhaba, program davet kodum yeniden gönderildi ancak e-postama ulaşmadı. Destek rica ediyorum.",
)}`;

const maskEmail = (email = "") => {
  const [localPart, domain] = email.split("@");
  if (!localPart || !domain) return "Kayıtlı e-posta adresiniz";
  return `${localPart.slice(0, 1)}•••@${domain}`;
};

export default function ProgramRequestPage() {
  const { user } = useAuth();
  const location = useLocation();
  const program = useMemo(() => getProgramFromSearch(location.search), [location.search]);
  const requestSnapshotKnown = location.state?.requestKnown === true;
  const [request, setRequest] = useState(
    requestSnapshotKnown ? location.state?.request || null : null,
  );
  const [loading, setLoading] = useState(!requestSnapshotKnown);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState("");
  const [requestResultOpen, setRequestResultOpen] = useState(false);
  const [error, setError] = useState("");
  const requestMutatedRef = useRef(false);

  useEffect(() => {
    let active = true;
    api
      .get("/client-portal/automation")
      .then(({ data }) => {
        if (active && !requestMutatedRef.current) setRequest(data?.request || null);
      })
      .catch(() => {})
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  if (!program) return <Navigate to="/danismanlik" replace />;

  const submitRequest = async () => {
    if (submitting || request?.status === "submitted" || request?.status === "reviewed") return;
    requestMutatedRef.current = true;
    setSubmitting(true);
    setError("");
    try {
      const idempotencyKey = `program-request:${globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`}`;
      const { data } = await api.post(
        "/client-portal/program-requests",
        { package_months: program.months },
        { headers: { "Idempotency-Key": idempotencyKey } },
      );
      setRequest(data?.request || data);
      setRequestResultOpen(true);
    } catch (requestError) {
      if (requestError?.response?.status === 409) {
        setError(
          requestError.response.data?.detail ||
            "Hesabınızda açık veya aktif bir program bulunuyor.",
        );
      } else {
        setError(formatApiError(requestError));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const status = requestStatusLabels[request?.status];
  const canResendInvitation = ["submitted", "reviewed"].includes(request?.status);
  const invitationResent = Number(request?.invitation_resend_count || 0) >= 1;
  const statusMessage =
    request?.status === "approved"
      ? "Başlangıç adımlarını danışan panelinizden takip edebilirsiniz."
      : request?.status === "code_redeemed"
        ? "Başlangıç anketinizi danışan panelinizden tamamlayabilirsiniz."
        : request?.status === "declined"
          ? "Ayrıntılı durum bilgisi danışan panelinizde gösterilir."
          : request?.invitation_email_status === "sent"
            ? "Paketinize özel davet kodu kayıtlı e-posta adresinize gönderildi."
            : "Paketinize özel davet kodu kayıtlı e-posta adresinize gönderiliyor.";

  const resendInvitation = async () => {
    if (!request?.id || resending) return;
    setResending(true);
    setResendMessage("");
    try {
      const { data } = await api.post(
        `/client-portal/program-requests/${request.id}/resend-invitation`,
      );
      if (data?.request) setRequest(data.request);
      setResendMessage(
        data?.status === "support_required"
          ? "İkinci gönderim için WhatsApp üzerinden destek alabilirsiniz."
          : "Davet kodunuz e-posta adresinize yeniden gönderiliyor.",
      );
    } catch (requestError) {
      setResendMessage(formatApiError(requestError));
    } finally {
      setResending(false);
    }
  };
  return (
    <>
      <Dialog open={requestResultOpen && canResendInvitation} onOpenChange={setRequestResultOpen}>
        <DialogContent className="left-0 right-0 bottom-0 top-auto w-full max-w-none translate-x-0 translate-y-0 rounded-b-none rounded-t-2xl border-pleiades-border bg-white p-5 sm:left-[50%] sm:right-auto sm:bottom-auto sm:top-[50%] sm:max-w-md sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-xl">
          <div className="mx-auto grid h-12 w-12 place-items-center rounded-full border border-pleiades-border bg-pleiades-surface text-pleiades-ink">
            <Clock3 size={24} aria-hidden="true" />
          </div>
          <DialogHeader className="text-center sm:text-center">
            <span className="text-xs font-semibold tracking-[0.16em] text-pleiades-muted">
              TALEBİNİZ ALINDI
            </span>
            <DialogTitle>{status}</DialogTitle>
            <DialogDescription>{statusMessage}</DialogDescription>
          </DialogHeader>
          <div className="mt-1 grid gap-2">
            {!invitationResent ? (
              <button
                type="button"
                className="stitch-secondary-button"
                disabled={resending}
                onClick={resendInvitation}
              >
                {resending ? "Yeniden gönderiliyor…" : "Davet kodumu almadım"}
              </button>
            ) : (
              <a
                href={SUPPORT_WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                className="stitch-secondary-button"
              >
                <MessageCircle size={17} aria-hidden="true" />
                Kod hâlâ gelmediyse WhatsApp’tan yaz
              </a>
            )}
            {resendMessage && <p role="status" className="stitch-request-note">{resendMessage}</p>}
            <Link to="/danisan-paneli" className="stitch-primary-button">
              Danışan paneline git
            </Link>
            <button
              type="button"
              className="stitch-secondary-button"
              onClick={() => setRequestResultOpen(false)}
            >
              Pencereyi kapat
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <StitchFlowLayout
      backTo={`/danismanlik/${program.slug}`}
      backLabel="Program ayrıntılarına dön"
    >
      <section className="stitch-request-page">
        <header className="stitch-request-heading">
          <h2>Davet kodu iste</h2>
          <p>
            {program.label} talebiniz incelendikten sonra davet kodunuz kayıtlı e-posta
            adresinize gönderilecektir.
          </p>
        </header>

        <div className="stitch-request-details">
          <div className="stitch-request-selection">
            <span>Seçilen program</span>
            <strong>{program.label}</strong>
          </div>
          <label className="stitch-request-email">
            <span>E-posta adresi</span>
            <input readOnly value={maskEmail(user?.email)} />
          </label>
          <p className="stitch-request-note">
            <Info size={16} aria-hidden="true" />
            Kod yalnız bu program ve hesabınız için geçerlidir.
          </p>
        </div>

        <aside className="stitch-request-panel">
              {loading ? (
                <div role="status" aria-live="polite" className="stitch-request-state">
                  <Clock3 size={24} aria-hidden="true" />
                  <h2>Talep durumunuz kontrol ediliyor…</h2>
                  <p>Bu kontrol tamamlandığında işlem alanınız hemen açılacak.</p>
                </div>
              ) : status ? (
                <div role="status" className="stitch-request-state">
                  {request.status === "approved" ? (
                    <CheckCircle2 size={28} />
                  ) : (
                    <Clock3 size={28} />
                  )}
                  <h2>{status}</h2>
                  <p>{statusMessage}</p>
                  {canResendInvitation && (
                    <div className="mt-4 grid gap-2">
                      {!invitationResent ? (
                        <button
                          type="button"
                          className="stitch-secondary-button"
                          disabled={resending}
                          onClick={resendInvitation}
                        >
                          {resending ? "Yeniden gönderiliyor…" : "Davet kodumu almadım"}
                        </button>
                      ) : (
                        <a
                          href={SUPPORT_WHATSAPP_URL}
                          target="_blank"
                          rel="noreferrer"
                          className="stitch-secondary-button"
                        >
                          <MessageCircle size={17} aria-hidden="true" />
                          Kod hâlâ gelmediyse WhatsApp’tan yaz
                        </a>
                      )}
                      {resendMessage && <p role="status">{resendMessage}</p>}
                    </div>
                  )}
                  {request.status === "declined" ? (
                    <button
                      type="button"
                      className="stitch-primary-button"
                      onClick={() => {
                        setRequest(null);
                        setError("");
                        setRequestResultOpen(false);
                      }}
                    >
                      Yeni talep oluştur
                    </button>
                  ) : (
                    <Link to="/danisan-paneli" className="stitch-primary-button">
                      Danışan paneline git
                    </Link>
                  )}
                </div>
              ) : (
                <>
                  {error && (
                    <div role="alert" className="stitch-form-error">
                      {error}
                    </div>
                  )}
                  <button
                    type="button"
                    className="stitch-primary-button"
                    disabled={submitting}
                    onClick={submitRequest}
                  >
                    {submitting ? "Davet kodu hazırlanıyor…" : "Davet kodu talebi oluştur"}
                  </button>
                  <Link
                    to="/danisan-paneli?sekme=danismanlik-programlari"
                    className="stitch-secondary-button"
                  >
                    Programı değiştir
                  </Link>
                </>
              )}
        </aside>
      </section>
      </StitchFlowLayout>
    </>
  );
}
